import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload, ArrowRight, Loader2, AlertTriangle, CheckCircle2, Download } from "lucide-react";
import { api, formatApiError } from "@/api/client";

const TARGETS = ["first_name", "last_name", "phone", "email", "dob", "address", "notes", "person_types", "gender"];

export default function ImportPeople() {
    const { t } = useTranslation();
    const [step, setStep] = useState<"upload" | "map" | "result">("upload");
    const [batch, setBatch] = useState<any>(null);
    const [headers, setHeaders] = useState<string[]>([]);
    const [columnMap, setColumnMap] = useState<Record<string, string | null>>({});
    const [dupPolicy, setDupPolicy] = useState<"skip" | "warn_create" | "update">("skip");
    const [err, setErr] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [duplicateOf, setDuplicateOf] = useState<{ id: string; filename: string } | null>(null);

    const upload = async (file: File) => {
        setBusy(true); setErr(null);
        try {
            const fd = new FormData(); fd.append("file", file);
            const { data } = await api.post("/import/people/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setBatch(data);
            setHeaders(data.headers || []);
            setColumnMap(data.column_map || {});
            setDuplicateOf(data.duplicate_of ? { id: data.duplicate_of, filename: data.duplicate_of_filename } : null);
            setStep("map");
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const validate = async () => {
        if (!batch) return;
        setBusy(true); setErr(null);
        try {
            const { data } = await api.post(`/import/people/${batch.id}/validate`, { column_map: columnMap, dup_policy: dupPolicy });
            setBatch(data);
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const apply = async () => {
        if (!batch) return;
        setBusy(true); setErr(null);
        try {
            const { data } = await api.post(`/import/people/${batch.id}/apply`);
            setBatch(data);
            setStep("result");
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const errorsCsv = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/import/people/${batch.id}/errors.csv`, "_blank");

    return (
        <div className="space-y-4 max-w-4xl" data-testid="import-page">
            <h1 className="text-2xl font-semibold">{t("import.title")}</h1>
            <div className="flex items-center gap-2 text-xs">
                {(["upload", "map", "result"] as const).map((s, i) => (
                    <div key={s} className="inline-flex items-center gap-2">
                        <span className="rounded-full px-2 py-0.5" style={{ background: step === s ? "var(--primary)" : "var(--input)", color: step === s ? "var(--primary-fg)" : "var(--text-muted)" }}>{i + 1}. {t(`import.step.${s}`)}</span>
                        {i < 2 && <ArrowRight size={12} style={{ color: "var(--text-muted)" }} />}
                    </div>
                ))}
            </div>
            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="import-error">{err}</div>}

            {step === "upload" && (
                <div className="rounded-xl border p-5 space-y-3" style={{ background: "var(--card)", borderColor: "var(--border)" }}>
                    <label className="block text-sm">{t("import.pick_file")}
                        <input data-testid="import-file" type="file" accept=".csv,text/csv" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} className="mt-2 block text-sm" />
                    </label>
                    <p className="text-xs" style={{ color: "var(--text-muted)" }}>{t("import.help_upload")}</p>
                    {busy && <Loader2 className="animate-spin" size={18} />}
                </div>
            )}

            {step === "map" && batch && (
                <div className="rounded-xl border p-5 space-y-4" style={{ background: "var(--card)", borderColor: "var(--border)" }}>
                    {duplicateOf && (
                        <div className="rounded-md p-3 text-sm flex items-start gap-2" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }} data-testid="import-duplicate-warn">
                            <AlertTriangle size={16} />
                            <div>{t("import.duplicate_warn", { filename: duplicateOf.filename })}</div>
                        </div>
                    )}
                    <div className="space-y-2">
                        <h2 className="text-sm font-semibold">{t("import.step.map")}</h2>
                        {TARGETS.map((f) => (
                            <div key={f} className="flex items-center gap-3">
                                <div className="w-40 text-sm">{t(`import.field.${f}`)}</div>
                                <select data-testid={`import-map-${f}`} value={columnMap[f] || ""} onChange={(e) => setColumnMap({ ...columnMap, [f]: e.target.value || null })} className="flex-1 rounded-md border px-3 py-2 text-sm" style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }}>
                                    <option value="">— {t("import.skip")} —</option>
                                    {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                                </select>
                            </div>
                        ))}
                    </div>
                    <label className="block text-sm">{t("import.dup_policy")}
                        <select data-testid="import-dup-policy" value={dupPolicy} onChange={(e) => setDupPolicy(e.target.value as any)} className="mt-1 w-full rounded-md border px-3 py-2 text-sm" style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }}>
                            <option value="skip">{t("import.dup.skip")}</option>
                            <option value="warn_create">{t("import.dup.warn_create")}</option>
                            <option value="update">{t("import.dup.update")}</option>
                        </select>
                    </label>
                    <div className="flex gap-2">
                        <button data-testid="import-validate" disabled={busy} onClick={validate} className="rounded-md px-4 py-2 text-sm font-medium" style={{ background: "var(--input)", color: "var(--text)", borderColor: "var(--border)" }}>{t("import.validate")}</button>
                        <button data-testid="import-apply" disabled={busy || batch.status !== "validated"} onClick={apply} className="rounded-md px-4 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", opacity: batch.status !== "validated" ? 0.5 : 1 }}>{t("import.apply")}</button>
                    </div>
                    {batch.counters && (
                        <div className="text-sm space-y-1" data-testid="import-counters">
                            <div><span style={{ color: "var(--text-muted)" }}>{t("import.total_rows")}:</span> {batch.counters.total_rows}</div>
                            {batch.counters.valid_rows != null && <div><span style={{ color: "var(--text-muted)" }}>{t("import.valid_rows")}:</span> {batch.counters.valid_rows}</div>}
                            {batch.counters.error_rows != null && <div><span style={{ color: "var(--text-muted)" }}>{t("import.error_rows")}:</span> {batch.counters.error_rows}</div>}
                            {batch.counters.duplicate_rows != null && <div><span style={{ color: "var(--text-muted)" }}>{t("import.duplicate_rows")}:</span> {batch.counters.duplicate_rows}</div>}
                        </div>
                    )}
                    {batch.errors && batch.errors.length > 0 && (
                        <div>
                            <button onClick={errorsCsv} data-testid="import-errors-csv" className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs border" style={{ borderColor: "var(--border)", color: "var(--text)" }}><Download size={12} />{t("import.download_errors")}</button>
                        </div>
                    )}
                </div>
            )}

            {step === "result" && batch && (
                <div className="rounded-xl border p-6 space-y-3" style={{ background: "var(--card)", borderColor: "var(--border)" }} data-testid="import-result">
                    <div className="flex items-center gap-2 text-lg font-semibold" style={{ color: "var(--success-fg)" }}><CheckCircle2 size={22} />{t("import.done")}</div>
                    <div className="text-sm space-y-1">
                        <div>{t("import.created")}: <span className="font-semibold">{batch.counters?.created || 0}</span></div>
                        <div>{t("import.updated")}: <span className="font-semibold">{batch.counters?.updated || 0}</span></div>
                        <div>{t("import.skipped")}: <span className="font-semibold">{batch.counters?.skipped || 0}</span></div>
                    </div>
                </div>
            )}
        </div>
    );
}
